'use client'

import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { setCookie } from 'cookies-next'
import { LANGUAGES, LanguageCode, DEFAULT_LANGUAGE } from '~/data/language'

interface LocaleContextType {
  locale: LanguageCode
  setLocale: (locale: LanguageCode) => void
  toggleLocale: () => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const urlLocaleRaw = pathname.split('/')[1] as LanguageCode
  const urlLocale = LANGUAGES.includes(urlLocaleRaw) ? urlLocaleRaw : DEFAULT_LANGUAGE
  const [locale, setLocaleState] = useState<LanguageCode>(urlLocale)

  // Sync locale with URL
  useEffect(() => {
    if (LANGUAGES.includes(urlLocaleRaw)) {
      setLocaleState(urlLocaleRaw)
    } else {
      setLocaleState(DEFAULT_LANGUAGE)
    }
  }, [urlLocaleRaw])

  const setLocale = useCallback(
    (newLocale: LanguageCode) => {
      if (!LANGUAGES.includes(newLocale)) return

      setLocaleState(newLocale)
      setCookie('NEXT_LOCALE', newLocale)

      // Update URL with new locale
      const segments = pathname.split('/')
      segments[1] = newLocale as string
      const newPath = segments.join('/')
      router.push(newPath)
    },
    [pathname, router]
  )

  const toggleLocale = useCallback(() => {
    const currentIndex = LANGUAGES.indexOf(locale)
    const nextIndex = (currentIndex + 1) % LANGUAGES.length
    const newLocale = LANGUAGES[nextIndex]
    setLocale(newLocale)
  }, [locale, setLocale])

  return (
    <LocaleContext.Provider value={{ locale, setLocale, toggleLocale }}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}
